/** Daily spend of a campaign: one series of thin bars (brand token), hover title, table view below. */

import type { AdStat } from "@agencyos/api-client";
import { formatXaf } from "@agencyos/core";
import { useTranslation } from "react-i18next";

import { dateText } from "../billing/shared";

const WIDTH = 640;
const HEIGHT = 180;
const PAD_TOP = 12;
const PAD_BOTTOM = 22;
const GAP = 2;

export function SpendChart({ stats }: { stats: readonly AdStat[] }) {
  const { t, i18n } = useTranslation();
  const first = stats[0];
  if (!first) return null;
  const max = Math.max(...stats.map((s) => s.spend_xaf), 1);
  const slot = WIDTH / stats.length;
  const bar = Math.max(Math.min(slot - GAP, 28), 2);
  const plot = HEIGHT - PAD_TOP - PAD_BOTTOM;
  const peak = stats.reduce((best, s) => (s.spend_xaf > best.spend_xaf ? s : best), first);
  return (
    <figure className="ads-chart">
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="img" aria-label={t("ads.detail.chart")}>
        <line className="ads-chart__axis" x1={0} x2={WIDTH} y1={HEIGHT - PAD_BOTTOM} y2={HEIGHT - PAD_BOTTOM} />
        {stats.map((s, n) => {
          const h = Math.max((s.spend_xaf / max) * plot, s.spend_xaf ? 2 : 0);
          const x = n * slot + (slot - bar) / 2;
          const y = HEIGHT - PAD_BOTTOM - h;
          return (
            <g key={s.day} className="ads-chart__slot">
              <rect className="ads-chart__hit" x={n * slot} y={0} width={slot} height={HEIGHT - PAD_BOTTOM} />
              <path className="ads-chart__bar" d={`M${x},${y + h} V${y + Math.min(4, h)} Q${x},${y} ${x + Math.min(4, bar / 2)},${y} H${x + bar - Math.min(4, bar / 2)} Q${x + bar},${y} ${x + bar},${y + Math.min(4, h)} V${y + h} Z`} />
              <title>{`${dateText(s.day, i18n.language)} · ${formatXaf(s.spend_xaf)}`}</title>
            </g>
          );
        })}
      </svg>
      <figcaption className="muted">
        {t("ads.detail.chart")} · {dateText(peak.day, i18n.language)} : {formatXaf(peak.spend_xaf)}
      </figcaption>
    </figure>
  );
}
